import { useEffect, useMemo, useState } from "react";
import { fetchWisdomODBMDashboard } from "services/wisdom/wisdomData";

export const COLORS = { exc: "#22B07D", mid: "#E0A32B", low: "#D8543F", none: "#4E627E" };

export const TABS = [
  { key: "sessions", label: "الحصص المنفذة", tgt: 95, unitLabel: "حسب القسم", hasTrend: false },
  { key: "STA", label: "حضور الطلاب", tgt: 90, unitLabel: "حسب التخصص", hasTrend: true },
  { key: "STB", label: "ضبط الطلاب", tgt: 95, unitLabel: "حسب التخصص", hasTrend: true },
  { key: "DO", label: "الطابور الصباحي", tgt: 90, unitLabel: "حسب القسم", hasTrend: true },
];

const AR_DIGITS = "٠١٢٣٤٥٦٧٨٩";
const ar = (n) => (n == null ? "—" : String(Math.round(n)).replace(/[0-9]/g, (d) => AR_DIGITS[+d]));
export const fmt = (v) => (v == null ? "—" : `${ar(v)}٪`);
export const bandColor = (v, tgt) => (v == null ? COLORS.none : v >= tgt ? COLORS.exc : v >= tgt - 10 ? COLORS.mid : COLORS.low);
const mean = (arr) => {
  const v = (arr || []).filter((x) => x != null);
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
};
const clip = (v) => Math.max(0, Math.min(100, v));

const AR_MONTHS = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
const AR_WEEKDAYS_SCHOOL = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس"]; // getDay() 0..4, school week (Fri/Sat off)

// School week starts Saturday.
function weekStart(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 1) % 7));
  return d;
}

// Days (of month) covered by the trend charts: from day 1 up to today (current month)
// or the end of the month (past months), capped to the most recent 14.
function buildDayRange(year, month) {
  const y = Number(year);
  const m = Number(month) || 0;
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === y && today.getMonth() === m;
  const lastDay = isCurrentMonth ? today.getDate() : daysInMonth;
  const all = Array.from({ length: Math.max(lastDay, 1) }, (_, i) => i + 1);
  return all.slice(-14);
}

export function buildStats(dashboard, year, month) {
  if (!dashboard) return null;

  const days = buildDayRange(year, month);

  // ---- الحصص المنفذة: cumulative planned/actual per teacher, grouped by department ----
  // No per-day history is queried yet, so this indicator has no daily trend for now.
  const teachersSessions = dashboard.teachersSessions || [];
  let plannedTotal = 0;
  let actualTotal = 0;
  const sessionsByDept = {};
  teachersSessions.forEach((t) => {
    const planned = Number(t.planned_sessions) || 0;
    // Cap per teacher so bad backend data (actual > planned) can't push the % above 100.
    const actual = Math.min(Number(t.actual_sessions) || 0, planned);
    plannedTotal += planned;
    actualTotal += actual;

    const deptName = t.department?.Name || "غير محدد";
    sessionsByDept[deptName] ??= { planned: 0, actual: 0 };
    sessionsByDept[deptName].planned += planned;
    sessionsByDept[deptName].actual += actual;
  });
  const sessionsOverall = plannedTotal ? Math.round((actualTotal / plannedTotal) * 100) : null;
  const sessionsUnits = Object.entries(sessionsByDept)
    .map(([name, v]) => [name, v.planned ? Math.round((v.actual / v.planned) * 100) : 0])
    .sort((a, b) => b[1] - a[1]);

  // ---- حضور الطلاب: daily % present (attend + late), grouped by specialization ----
  const attendance = dashboard.studentsAttendance || [];
  const isPresent = (status) => status === "attend" || status === "late";
  const attByDay = {};
  const attBySpec = {};
  attendance.forEach((r) => {
    const day = new Date(r.createdAt).getDate();
    attByDay[day] ??= { present: 0, total: 0 };
    attByDay[day].total += 1;
    if (isPresent(r.status)) attByDay[day].present += 1;

    const specName = r.student?.specialization?.name || "غير محدد";
    attBySpec[specName] ??= { present: 0, total: 0 };
    attBySpec[specName].total += 1;
    if (isPresent(r.status)) attBySpec[specName].present += 1;
  });
  const attDaily = days.map((day) => {
    const b = attByDay[day];
    return b && b.total ? Math.round((b.present / b.total) * 100) : null;
  });
  const attUnits = Object.entries(attBySpec)
    .map(([name, v]) => [name, v.total ? Math.round((v.present / v.total) * 100) : 0])
    .sort((a, b) => b[1] - a[1]);

  // ---- ضبط الطلاب: daily compliance = 100 - (incidents / total students * 100), grouped by specialization ----
  const behavior = dashboard.studentsBehavior || [];
  const allStudents = dashboard.allStudents || [];
  const totalStudents = allStudents.length;
  const studentsBySpec = {};
  allStudents.forEach((s) => {
    const specName = s.specialization?.name || "غير محدد";
    studentsBySpec[specName] = (studentsBySpec[specName] || 0) + 1;
  });
  const incidentsByDay = {};
  const incidentsBySpec = {};
  behavior.forEach((b) => {
    const day = new Date(b.behavior_date).getDate();
    incidentsByDay[day] = (incidentsByDay[day] || 0) + 1;

    const specName = b.offender?.student?.specialization?.name || "غير محدد";
    incidentsBySpec[specName] = (incidentsBySpec[specName] || 0) + 1;
  });
  const disDaily = days.map((day) => {
    if (!totalStudents) return null;
    const incidents = incidentsByDay[day] || 0;
    return Math.round(clip(100 - (incidents / totalStudents) * 100));
  });
  const disUnits = Object.entries(studentsBySpec)
    .map(([name, total]) => {
      const incidents = incidentsBySpec[name] || 0;
      return [name, total ? Math.round(clip(100 - (incidents / total) * 100)) : 100];
    })
    .sort((a, b) => b[1] - a[1]);

  // ---- الطابور الصباحي: daily avg of submitted form scores, grouped by department ----
  const morningLine = dashboard.morningLineScores || [];
  const doByDay = {};
  const doByDept = {};
  morningLine.forEach((r) => {
    const day = new Date(r.createdAt).getDate();
    doByDay[day] ??= [];
    doByDay[day].push(r.avg_score);

    const deptName = r.employee?.employee?.teacher?.department?.Name || "غير محدد";
    doByDept[deptName] ??= [];
    doByDept[deptName].push(r.avg_score);
  });
  const doDaily = days.map((day) => {
    const arr = doByDay[day];
    return arr && arr.length ? Math.round(mean(arr)) : null;
  });
  const doUnits = Object.entries(doByDept)
    .map(([name, arr]) => [name, Math.round(mean(arr) ?? 0)])
    .sort((a, b) => b[1] - a[1]);

  // ---- الطابور الصباحي: weekly matrix per department (last 4 weeks in the month) ----
  const weekBuckets = {};
  morningLine.forEach((r) => {
    const wk = weekStart(r.createdAt).toISOString().slice(0, 10);
    const deptName = r.employee?.employee?.teacher?.department?.Name || "غير محدد";
    weekBuckets[wk] ??= {};
    weekBuckets[wk][deptName] ??= [];
    weekBuckets[wk][deptName].push(r.avg_score);
  });
  const weekKeys = Object.keys(weekBuckets).sort().slice(-4);
  const weekLabels = weekKeys.map((k) => {
    const d = new Date(k);
    return `${ar(d.getDate())} ${AR_MONTHS[d.getMonth()]}`;
  });
  const deptNames = new Set();
  weekKeys.forEach((k) => Object.keys(weekBuckets[k]).forEach((n) => deptNames.add(n)));
  const doMatrix = Array.from(deptNames)
    .map((name) => ({
      lb: name,
      v: weekKeys.map((k) => {
        const arr = weekBuckets[k][name];
        return arr && arr.length ? Math.round(mean(arr)) : null;
      }),
    }))
    .filter((r) => r.v[r.v.length - 1] != null);

  // ---- الطابور الصباحي: average by weekday (school week only) ----
  const wdBuckets = {};
  morningLine.forEach((r) => {
    const dow = new Date(r.createdAt).getDay();
    if (dow === 5 || dow === 6) return;
    wdBuckets[dow] ??= [];
    wdBuckets[dow].push(r.avg_score);
  });
  const doWeekday = AR_WEEKDAYS_SCHOOL.map((lb, i) => {
    const arr = wdBuckets[i];
    return [lb, arr && arr.length ? Math.round(mean(arr)) : null];
  });

  return {
    days,
    sessions: { overall: sessionsOverall, units: sessionsUnits },
    STA: { daily: attDaily, units: attUnits },
    STB: { daily: disDaily, units: disUnits },
    DO: { daily: doDaily, units: doUnits, weekLabels, matrix: doMatrix, weekday: doWeekday },
  };
}

export function Kpi({ label, value, color }) {
  return (
    <div className="rounded-lg border border-[#26385A] bg-[#16233A] px-2 py-1.5 text-center">
      <span className="block text-[9.5px] leading-tight text-[#6C82A3]">{label}</span>
      <b className="mt-0.5 block text-[17px] font-semibold leading-tight tabular-nums" style={{ color }}>
        {value}
      </b>
    </div>
  );
}

export function Ranking({ tab, units, showScale = false }) {
  return (
    <section className="rounded-xl border border-[#26385A] bg-[#16233A] px-2.5 py-2.5">
      <h2 className="m-0 mb-2 flex flex-wrap items-center gap-1.5 text-[11.5px] font-semibold text-[#9DB2CE]">
        {tab.label} {tab.unitLabel}
        <em className="text-[10px] font-normal not-italic text-[#6C82A3]">الخط الرأسي = المستهدف {ar(tab.tgt)}٪</em>
      </h2>
      <div className="flex flex-col gap-0.5">
        {units.map(([name, v]) => (
          <div
            key={name}
            className="grid grid-cols-[112px_1fr_42px] items-center gap-2 rounded-md p-[3px] hover:bg-[#1C2C47]"
          >
            <span className="truncate text-[10px] text-[#9DB2CE]" title={name}>
              {name}
            </span>
            <span className="relative block h-3 overflow-hidden rounded-l-[3px] bg-[#1C2C47]">
              <i
                className="block h-full rounded-l-[3px] transition-[width] duration-500 motion-reduce:transition-none"
                style={{ width: `${v}%`, background: bandColor(v, tab.tgt) }}
              />
              <i
                className="absolute -top-0.5 -bottom-0.5 w-[1.5px] bg-[#9DB2CE] opacity-65"
                style={{ right: `${tab.tgt}%` }}
              />
            </span>
            <span className="text-left text-[10.5px] font-semibold tabular-nums" style={{ color: bandColor(v, tab.tgt) }}>
              {ar(v)}٪
            </span>
          </div>
        ))}
        {!units.length && <p className="py-3 text-center text-[10.5px] text-[#6C82A3]">لا توجد بيانات كافية.</p>}
      </div>
      {showScale && !!units.length && (
        <div className="mt-1.5 grid grid-cols-[112px_1fr_42px] gap-2">
          <div />
          <div className="relative h-3 border-t border-[#26385A]">
            {[0, 50, 100].map((p) => (
              <span key={p} className="absolute top-0 translate-x-1/2 text-[9px] text-[#6C82A3]" style={{ right: `${p}%` }}>
                {ar(p)}٪
              </span>
            ))}
          </div>
          <div />
        </div>
      )}
    </section>
  );
}

function Legend() {
  return (
    <div className="mt-1.5 flex flex-wrap gap-2 text-[9.5px] text-[#9DB2CE]">
      <span className="flex items-center gap-1">
        <b className="block h-[7px] w-[7px] rounded-sm" style={{ background: COLORS.exc }} />
        عند المستهدف أو أعلى
      </span>
      <span className="flex items-center gap-1">
        <b className="block h-[7px] w-[7px] rounded-sm" style={{ background: COLORS.mid }} />
        أقل من المستهدف
      </span>
      <span className="flex items-center gap-1">
        <b className="block h-[7px] w-[7px] rounded-sm" style={{ background: COLORS.low }} />
        أقل من المستهدف بعشر نقاط
      </span>
      <span className="flex items-center gap-1">
        <b className="block h-[7px] w-[7px] rounded-sm border-[1.5px] border-dashed" style={{ borderColor: COLORS.none }} />
        يوم غير مسجَّل
      </span>
    </div>
  );
}

function TrendKpis({ tab, today, week, trend }) {
  return (
    <div className="mb-2.5 grid grid-cols-2 gap-1.5 sm:grid-cols-4">
      <Kpi label="اليوم" value={fmt(today)} color={bandColor(today, tab.tgt)} />
      <Kpi label="متوسط ٧ أيام" value={fmt(week)} color={bandColor(week, tab.tgt)} />
      <Kpi label="المستهدف" value={`${ar(tab.tgt)}٪`} color="#9DB2CE" />
      <Kpi
        label="الاتجاه"
        value={trend == null ? "—" : `${trend > 0 ? "▲ +" : trend < 0 ? "▼ −" : "—"}${ar(Math.abs(trend))}`}
        color={trend == null ? "#6C82A3" : trend >= 0 ? COLORS.exc : COLORS.low}
      />
    </div>
  );
}

function DailyChart({ tab, days, daily }) {
  const periodAvg = mean(daily);
  return (
    <section className="mb-2.5 rounded-xl border border-[#26385A] bg-[#16233A] px-2.5 py-2.5">
      <h2 className="m-0 mb-2 flex flex-wrap items-center gap-1.5 text-[11.5px] font-semibold text-[#9DB2CE]">
        {tab.label} · آخر {ar(days.length)} يوماً
        <em className="text-[10px] font-normal not-italic text-[#6C82A3]">متوسط الفترة {fmt(periodAvg)}</em>
      </h2>

      <div className="relative flex h-[132px] items-end gap-[3px] border-b border-[#26385A] pt-1.5">
        <div
          className="absolute inset-x-0 border-t-[1.5px] border-dashed border-[rgba(157,178,206,0.5)]"
          style={{ bottom: `${tab.tgt * 0.82}%` }}
        >
          <span className="absolute start-0 -top-[13px] whitespace-nowrap bg-[#16233A] px-1 text-[8.5px] text-[#9DB2CE]">
            المستهدف {ar(tab.tgt)}٪
          </span>
        </div>
        {days.map((day, i) => {
          const v = daily[i];
          return (
            <div key={day} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end">
              {v == null ? (
                <i
                  className="block w-full rounded-t-[3px] border-[1.5px] border-b-0 border-dashed"
                  style={{ height: "22%", borderColor: COLORS.none }}
                />
              ) : (
                <>
                  <span
                    className="mb-0.5 whitespace-nowrap text-[8.5px] tabular-nums"
                    style={{ color: bandColor(v, tab.tgt) }}
                  >
                    {ar(v)}
                  </span>
                  <i
                    className="block w-full rounded-t-[3px] transition-[height] duration-500 motion-reduce:transition-none"
                    style={{ height: `${v * 0.82}%`, background: bandColor(v, tab.tgt) }}
                  />
                </>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-1.5 flex gap-[3px]">
        {days.map((day) => (
          <span key={day} className="flex-1 text-center text-[8px] text-[#6C82A3]">
            {ar(day)}
          </span>
        ))}
      </div>
      <Legend />
    </section>
  );
}

export function TrendTab({ tab, days, daily }) {
  const today = daily.length ? daily[daily.length - 1] : null;
  const week = mean(daily.slice(-7));
  const prevWeek = daily.length > 7 ? mean(daily.slice(0, daily.length - 7).slice(-7)) : null;
  const trend = week != null && prevWeek != null ? week - prevWeek : null;
  const missing = daily.filter((v) => v == null).length;

  return (
    <>
      <TrendKpis tab={tab} today={today} week={week} trend={trend} />

      {today == null && (
        <div className="mb-2.5 rounded-lg border border-[rgba(216,84,63,0.34)] bg-[rgba(216,84,63,0.10)] px-2.5 py-2 text-[10px] leading-relaxed text-[#F0B4AA]">
          <b className="mb-0.5 block font-semibold text-[#FFD3CA]">لم تُسجَّل قراءة اليوم لهذا المؤشر</b>
          {missing > 1 ? `عدد الأيام غير المسجَّلة في الفترة المعروضة ${ar(missing)}.` : null}
        </div>
      )}
      {week != null && week < tab.tgt ? (
        <div className="mb-2.5 rounded-lg border border-[rgba(224,163,43,0.34)] bg-[rgba(224,163,43,0.10)] px-2.5 py-2 text-[10px] leading-relaxed text-[#EFCE94]">
          <b className="mb-0.5 block font-semibold text-[#FBE6BC]">أقل من المستهدف بـ{ar(tab.tgt - week)} نقطة</b>
          متوسط الأسبوع {ar(week)}٪ مقابل مستهدف {ar(tab.tgt)}٪.
        </div>
      ) : week != null ? (
        <div className="mb-2.5 rounded-lg border border-[rgba(34,176,125,0.32)] bg-[rgba(34,176,125,0.10)] px-2.5 py-2 text-[10px] leading-relaxed text-[#9FE0C6]">
          <b className="mb-0.5 block font-semibold text-[#C6F0DF]">عند المستهدف</b>
          متوسط الأسبوع {ar(week)}٪ مقابل مستهدف {ar(tab.tgt)}٪.
        </div>
      ) : null}

      <DailyChart tab={tab} days={days} daily={daily} />
    </>
  );
}

function DeptWeekMatrix({ tab, weekLabels, rows }) {
  const now = weekLabels.length - 1;
  const sorted = rows.slice().sort((a, b) => a.v[now] - b.v[now]);

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[280px] border-separate [border-spacing:3px] table-fixed">
        <thead>
          <tr>
            <th className="w-[104px] pb-1 text-right text-[8.5px] font-medium leading-tight text-[#6C82A3]">القسم</th>
            {weekLabels.map((w, i) => (
              <th key={i} className="pb-1 text-center text-[8.5px] font-medium leading-tight text-[#9DB2CE]">
                {w}
              </th>
            ))}
            <th className="w-[38px] pb-1 text-center text-[8.5px] font-medium leading-tight text-[#6C82A3]">الاتجاه</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => {
            const ch = r.v[0] != null && r.v[now] != null ? r.v[now] - r.v[0] : null;
            return (
              <tr key={r.lb}>
                <td className="truncate pe-1 text-right text-[9px] leading-tight text-[#EAF1FA]" title={r.lb}>
                  {r.lb}
                </td>
                {r.v.map((v, i) => (
                  <td key={i}>
                    <div
                      className={`flex h-[29px] items-center justify-center rounded-md text-[11px] font-semibold tabular-nums ${
                        v != null && v < tab.tgt - 10 ? "shadow-[inset_0_0_0_1.5px_#D8543F]" : ""
                      }`}
                      style={{ background: v == null ? "transparent" : `${bandColor(v, tab.tgt)}22`, color: bandColor(v, tab.tgt) }}
                    >
                      {v == null ? "—" : `${ar(v)}٪`}
                    </div>
                  </td>
                ))}
                <td>
                  <div
                    className="flex h-[29px] items-center justify-center rounded-md border border-[#33507E] bg-[#1C2C47] text-[10px] font-semibold tabular-nums"
                    style={{ color: ch == null ? "#6C82A3" : ch > 0 ? COLORS.exc : ch < 0 ? COLORS.low : "#6C82A3" }}
                  >
                    {ch == null ? "—" : `${ch > 0 ? "▲" : ch < 0 ? "▼" : "—"}${ch ? ar(Math.abs(ch)) : ""}`}
                  </div>
                </td>
              </tr>
            );
          })}
          {!sorted.length && (
            <tr>
              <td colSpan={weekLabels.length + 2} className="py-3 text-center text-[10.5px] text-[#6C82A3]">
                لا توجد بيانات كافية.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function WeekdayChart({ tab, data }) {
  return (
    <>
      <div className="flex h-[96px] items-end gap-1.5 border-b border-[#26385A] pt-1">
        {data.map(([lb, v]) => (
          <div key={lb} className="flex h-full flex-1 flex-col items-center justify-end">
            <span className="mb-0.5 text-[9px] tabular-nums" style={{ color: bandColor(v, tab.tgt) }}>
              {v == null ? "—" : ar(v)}
            </span>
            {v == null ? (
              <i
                className="block w-full rounded-t-[4px] border-[1.5px] border-b-0 border-dashed"
                style={{ height: "10%", borderColor: COLORS.none }}
              />
            ) : (
              <i
                className="block w-full rounded-t-[4px] transition-[height] duration-500 motion-reduce:transition-none"
                style={{ height: `${v * 0.78}%`, background: bandColor(v, tab.tgt) }}
              />
            )}
          </div>
        ))}
      </div>
      <div className="mt-1.5 flex gap-1.5">
        {data.map(([lb]) => (
          <span key={lb} className="flex-1 text-center text-[9px] text-[#6C82A3]">
            {lb}
          </span>
        ))}
      </div>
    </>
  );
}

function MorningLineTab({ tab, days, daily, weekLabels, matrix, weekday, units }) {
  const today = daily.length ? daily[daily.length - 1] : null;
  const week = mean(daily.slice(-7));
  const prevWeek = daily.length > 7 ? mean(daily.slice(0, daily.length - 7).slice(-7)) : null;
  const trend = week != null && prevWeek != null ? week - prevWeek : null;

  const now = weekLabels.length - 1;
  const falls = matrix
    .filter((d) => d.v[0] != null && d.v[now] != null)
    .map((d) => ({ d, c: d.v[now] - d.v[0] }))
    .sort((a, b) => a.c - b.c);
  const worstFall = falls.length ? falls[0] : null;

  const wdKnown = weekday.filter(([, v]) => v != null);
  const wdLow = wdKnown.length ? wdKnown.slice().sort((a, b) => a[1] - b[1])[0] : null;
  const wdHigh = wdKnown.length ? wdKnown.slice().sort((a, b) => b[1] - a[1])[0] : null;

  return (
    <>
      <TrendKpis tab={tab} today={today} week={week} trend={trend} />

      {worstFall && worstFall.c < 0 && (
        <div className="mb-2.5 rounded-lg border border-[rgba(216,84,63,0.34)] bg-[rgba(216,84,63,0.10)] px-2.5 py-2 text-[10px] leading-relaxed text-[#F0B4AA]">
          <b className="mb-0.5 block font-semibold text-[#FFD3CA]">سبب التراجع: {worstFall.d.lb}</b>
          تراجع من {ar(worstFall.d.v[0])}٪ إلى {ar(worstFall.d.v[now])}٪ خلال {ar(weekLabels.length)} أسابيع — {ar(Math.abs(worstFall.c))} نقطة، وهو أكبر
          انخفاض بين الأقسام في هذه الفترة.
        </div>
      )}
      {wdLow && wdHigh && wdLow[1] < wdHigh[1] && (
        <div className="mb-2.5 rounded-lg border border-[rgba(224,163,43,0.34)] bg-[rgba(224,163,43,0.10)] px-2.5 py-2 text-[10px] leading-relaxed text-[#EFCE94]">
          <b className="mb-0.5 block font-semibold text-[#FBE6BC]">
            {wdLow[0]} أضعف أيام الأسبوع عند {ar(wdLow[1])}٪
          </b>
          مقابل {ar(wdHigh[1])}٪ يوم {wdHigh[0]} — فارق {ar(wdHigh[1] - wdLow[1])} نقطة يتكرر أسبوعياً.
        </div>
      )}

      <DailyChart tab={tab} days={days} daily={daily} />

      <div className="mb-2.5 grid gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,196px)]">
        <section className="rounded-xl border border-[#26385A] bg-[#16233A] px-2.5 py-2.5">
          <h2 className="m-0 mb-2 flex flex-wrap items-center gap-1.5 text-[11.5px] font-semibold text-[#9DB2CE]">
            الأقسام عبر الأسابيع <em className="text-[10px] font-normal not-italic text-[#6C82A3]">لقراءة الاتجاه</em>
          </h2>
          <DeptWeekMatrix tab={tab} weekLabels={weekLabels} rows={matrix} />
        </section>
        <section className="rounded-xl border border-[#26385A] bg-[#16233A] px-2.5 py-2.5">
          <h2 className="m-0 mb-2 text-[11.5px] font-semibold text-[#9DB2CE]">حسب يوم الأسبوع</h2>
          <WeekdayChart tab={tab} data={weekday} />
          <div className="mt-1.5 text-[9.5px] text-[#6C82A3]">متوسط كل يوم عبر الفترة المعروضة</div>
        </section>
      </div>

      <Ranking tab={tab} units={units} showScale />
    </>
  );
}

function SessionsTab({ tab, overall, units }) {
  const worst = units.length ? units.slice().sort((a, b) => a[1] - b[1])[0] : null;

  return (
    <>
      <div className="mb-2.5 grid grid-cols-2 gap-1.5">
        <Kpi label="النسبة الحالية" value={fmt(overall)} color={bandColor(overall, tab.tgt)} />
        <Kpi label="المستهدف" value={`${ar(tab.tgt)}٪`} color="#9DB2CE" />
      </div>

      <div className="mb-2.5 rounded-lg border border-[#26385A] bg-[rgba(46,147,204,0.08)] px-2.5 py-2 text-[10px] leading-relaxed text-[#9DB2CE]">
        الاتجاه اليومي لهذا المؤشر غير متاح حالياً — النسبة أعلاه إجمالية (الحصص الفعلية ÷ المخططة)، وسيُضاف السجل اليومي لاحقاً.
      </div>

      {overall != null && overall < tab.tgt && worst && (
        <div className="mb-2.5 rounded-lg border border-[rgba(224,163,43,0.34)] bg-[rgba(224,163,43,0.10)] px-2.5 py-2 text-[10px] leading-relaxed text-[#EFCE94]">
          <b className="mb-0.5 block font-semibold text-[#FBE6BC]">أقل من المستهدف بـ{ar(tab.tgt - overall)} نقطة</b>
          أضعف قسم: {worst[0]} عند {ar(worst[1])}٪.
        </div>
      )}
      {overall != null && overall >= tab.tgt && (
        <div className="mb-2.5 rounded-lg border border-[rgba(34,176,125,0.32)] bg-[rgba(34,176,125,0.10)] px-2.5 py-2 text-[10px] leading-relaxed text-[#9FE0C6]">
          <b className="mb-0.5 block font-semibold text-[#C6F0DF]">عند المستهدف</b>
          النسبة الحالية {ar(overall)}٪ مقابل مستهدف {ar(tab.tgt)}٪.
        </div>
      )}

      <Ranking tab={tab} units={units} />
    </>
  );
}

const ODBMDashboard = ({ orgId, year, month, onClose, page }) => {
  const [fetching, setFetching] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [cur, setCur] = useState(() => {
    const idx = TABS.findIndex((t) => t.key === page);
    return idx === -1 ? 0 : idx;
  });

  useEffect(() => {
    const load = async () => {
      try {
        setFetching(true);
        const response = await fetchWisdomODBMDashboard(orgId, year, month);
        console.log(response)
        setDashboard(response || null);
      } catch (err) {
        console.error("error:", err);
      } finally {
        setFetching(false);
      }
    };

    load();
  }, [orgId, year, month]);

  const stats = useMemo(() => buildStats(dashboard, year, month), [dashboard, year, month]);

  const tab = TABS[cur];

  return (
    <div
      className="fixed inset-0 z-[1000] grid place-items-center bg-[rgba(5,10,20,0.62)] p-4"
      style={{ fontFamily: '"Segoe UI", Tahoma, "Noto Sans Arabic", Arial, sans-serif' }}
    >
      <div
        dir="rtl"
        className="relative flex w-full max-w-[660px] max-h-[min(92vh,760px)] flex-col overflow-hidden rounded-2xl border border-[#33507E] bg-[#0F1A2B] text-[#EAF1FA] shadow-[0_24px_60px_rgba(0,0,0,0.5)]"
        role="dialog"
        aria-modal="true"
        aria-label="لوحة تحليل الانضباط العام"
      >
        <h1 className="sr-only">لوحة تحليل مؤشرات الانضباط العام الأربعة</h1>

        <div className="flex flex-none items-center gap-2 border-b border-[#26385A] bg-[#16233A] px-3.5 py-2.5">
          <div>
            <h1 className="m-0 text-sm font-semibold leading-snug">
              الانضباط العام
              <span className="ms-1 inline-block rounded-full border border-[rgba(46,147,204,0.32)] bg-[rgba(46,147,204,0.14)] px-2 py-0.5 align-[1px] text-[9.5px] text-[#8FCBEC]">
                بيانات عملية
              </span>
            </h1>
            <div className="mt-0.5 text-[10.5px] text-[#6C82A3]">
              {year ? `${Number(month || 0) + 1} / ${year}` : "كل الفترات"}
            </div>
          </div>
          <div className="ms-auto flex flex-none gap-1.5">
            <button
              type="button"
              aria-label="إغلاق"
              onClick={onClose}
              className="grid h-7 w-7 place-items-center rounded-lg border border-[#26385A] text-sm text-[#6C82A3] transition-colors hover:bg-[#1C2C47] hover:text-[#EAF1FA] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2E93CC] focus-visible:ring-offset-1"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3.5 pb-3.5 pt-2.5 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-lg [&::-webkit-scrollbar-thumb]:bg-[#33507E]">
          {fetching && <div className="px-2 py-6 text-center text-xs text-[#6C82A3]">جارِ تحميل البيانات...</div>}

          {!fetching && !stats && (
            <div className="px-2 py-6 text-center text-xs text-[#6C82A3]">لا توجد بيانات كافية لعرض التحليل.</div>
          )}

          {!fetching && stats && (
            <>
              <div className="mb-2.5 grid grid-cols-4 gap-1.5">
                {TABS.map((t, i) => {
                  const last = t.key === "sessions" ? stats.sessions.overall : stats[t.key].daily[stats[t.key].daily.length - 1] ?? null;
                  const on = i === cur;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setCur(i)}
                      className={`rounded-[10px] border px-1.5 py-2 text-center transition-colors ${
                        on ? "border-[#2E93CC] bg-[#1C2C47]" : "border-[#26385A] bg-[#16233A] hover:border-[#33507E]"
                      }`}
                    >
                      <span className="block min-h-[24px] text-[9.5px] leading-tight text-[#6C82A3]">{t.label}</span>
                      <b className="mt-0.5 block text-[16px] font-semibold tabular-nums" style={{ color: bandColor(last, t.tgt) }}>
                        {fmt(last)}
                      </b>
                    </button>
                  );
                })}
              </div>

              {tab.key === "DO" ? (
                <MorningLineTab
                  tab={tab}
                  days={stats.days}
                  daily={stats.DO.daily}
                  weekLabels={stats.DO.weekLabels}
                  matrix={stats.DO.matrix}
                  weekday={stats.DO.weekday}
                  units={stats.DO.units}
                />
              ) : tab.hasTrend ? (
                <>
                  <TrendTab tab={tab} days={stats.days} daily={stats[tab.key].daily} />
                  <Ranking tab={tab} units={stats[tab.key].units} />
                </>
              ) : (
                <SessionsTab tab={tab} overall={stats.sessions.overall} units={stats.sessions.units} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ODBMDashboard;
